// =============================================================================
// Optim TURPE V2 : LA CARTE DE TÊTE DU RÉSULTAT (contrat de l'interface v1.2,
// §4 ; maquette 2b jugée le 18/10/2026 ; décisions D1 b, D3 a, D4, D5, D7 a,
// D9 a, D10 a, D16 a, D17 a, D18 a, D20 a, A1, A2, A3, A7, A9).
//
// Ce que le courtier doit vivre : en cinq secondes, trois lignes. Le verdict,
// dont le montant SEUL porte le signe et la couleur (+ vert on gagne, −
// rouge on perd, « HT » et « par an » hors de la couleur) ; la raison de la
// décision, composée des valeurs servies (jamais « L'essentiel vient de … ») ;
// l'action. Une fiche de trois étiquettes au plus (Scénario, À demander, D'où
// vient le montant, ou en C5 « Pourquoi 11 kVA »), une seule ligne orange
// quand une action précède l'envoi (format court, A1), un constat gris sur
// « Rien à changer ». Au plus un bouton de niveau 1, un de niveau 2 et un
// lien de niveau 3 (R-T17, A2) ; « Télécharger le rapport » partout sauf sur
// « Vos puissances » (A9). Aucune case d'accord, aucune voix, aucune trace
// (D4, D5).
//
// Règles tenues ici : tout montant vient d'un champ servi, un montant absent
// ne s'écrit pas (jamais un 0) ; « par an » à 365 ou 366 jours seulement ;
// en C5, jamais « dépassement », « couvrir » ni « assumer » (D18).
// Module PUR : il ne lit que l'étude chargée et la paire.
// =============================================================================
import { formateurFr } from "@modules/courtage/utils/optim-turpe-v2.nombre"
import { lireChiffre } from "@modules/courtage/utils/optim-turpe-v2.chiffre"
import {
    lireDemande,
    type LigneDemande,
    referenceCourteEtude,
} from "@modules/courtage/utils/optim-turpe-v2.demande"
import {
    compositionDuMontant,
    etatDuParcours,
    type ContexteEntetes,
} from "@modules/courtage/utils/optim-turpe-v2.entetes"
import {
    formatNombreFr,
    getCmdpsEstimeeEur,
    getMeilleurePeriode,
    getUnitePuissance,
} from "@modules/courtage/utils/optim-turpe-v2.helpers"
import { lireArbitrageServi } from "@modules/courtage/utils/optim-turpe-v2.arbitrage"
import { libelleJoursMesures } from "@modules/courtage/utils/optim-turpe-v2.qualite"
import {
    CAUSES_NON_CHIFFRE_DE_MESURE,
    ECONOMIE_NON_CHIFFREE_SANS_CAUSE,
    LIBELLE_ETAT,
    LIBELLE_ETAT_CAUSE,
    LIBELLE_FORMULE_TARIFAIRE,
    LIBELLE_MONTANT_NON_SERVI,
    LIBELLE_SCENARIO_ARBITRAGE,
    LIGNE_DE_PERIMETRE,
    NOM_COURT_SCENARIO,
    VERDICT_NON_CHIFFRE_COURT,
} from "@modules/courtage/utils/optim-turpe-v2.libelles"
import {
    formatMontantSigne,
    nombreEuros,
    sensMontant,
} from "@modules/courtage/utils/optim-turpe-v2.montant"
import {
    CODE_RESERVE_C5_PIC,
    CODES_RESERVE_TITRE_C5,
    lireOptionSousLeSeuil,
    lireStatutStructure,
    montantC5,
    reserveTitreC5,
} from "@modules/courtage/utils/optim-turpe-v2.recommandation"
import {
    lireTitreDuResultat,
    montantDeTete,
    phrasePrixDeLaCouverture,
    surLaPeriode,
} from "@modules/courtage/utils/optim-turpe-v2.titre"
import {
    pointsDActions,
    texteDuRaccordement,
} from "@modules/courtage/utils/optim-turpe-v2.points"
import type { AncreAffiner } from "@modules/courtage/utils/optim-turpe-v2.completer"
import type { OptimTurpeV2Etude } from "@modules/courtage/types/optim-turpe-v2"

// La réserve C5 de Q22 se lit dans `recommandation.ts` (la conclusion et
// l'historique la lisent aussi) ; ces noms restent exportés d'ici.
export { CODE_RESERVE_C5_PIC, CODES_RESERVE_TITRE_C5, reserveTitreC5 }

/** L'état de la carte, lu sur l'état servi (une ligne du tableau des titres). */
export type EtatDeCarte =
    | "economie"
    | "gain_par_depassements_evites"
    | "rien_a_changer"
    | "option_sous_le_seuil"
    | "surcout"
    | "reserve_c5"
    | "non_chiffree"
    | "vos_puissances"
    /** Étude saisie dont la formule et les puissances égalent le contrat (A3). */
    | "vos_puissances_identiques"
    /** Le scénario affiché de la paire n'est pas le conseil établi (R2). */
    | "scenario_non_recommande"
    | "inconnu"

/** L'action de niveau 1 de la carte (une seule, R-T17). */
export type ActionDeCarte =
    | "preparer_demande"
    | "voir_resume"
    | "chiffrer_periode"
    | "voir_recommandation"
    | "modifier_hypotheses"
    | null

/** L'action de niveau 2 de la carte (une au plus, R-T17). */
export type ActionSecondaire =
    | "telecharger"
    | "preparer_demande"
    | "voir_resume"
    | null

/** Le titre : un texte, puis un montant signé et son unité, quand il y en a un. */
export type TitreDeCarte = {
    texte: string
    montantEur: number | null
    /** « par an », « sur la période du … au … ». */
    unite: string | null
    /** « soit environ +2 450 € HT par an » (Q15), ou `null`. */
    soitEnviron: string | null
}

/** Une valeur à demander au fournisseur : « HPB et HCB : 108 kVA → 121 kVA ». */
export type ValeurADemander = {
    libelle: string
    aujourdhui: string | null
    valeur: string
    /** Les codes des postes regroupés, `null` pour la formule et le C5. */
    postes?: string[] | null
    /** Le nombre de postes du compteur (« les cinq postes »). */
    tous?: number
}

/** La ligne « Scénario » de la fiche (A2) : nom, « Recommandé », dépassements. */
export type ScenarioDeLaFiche = {
    nom: string
    recommande: boolean
    detail: string | null
}

/** Un terme de « D'où vient le montant », en forme nominale (D1 b, A3). */
export type TermeAffiche = {
    nom: string
    /** « 2 355 € d'économie », « 1 399 € de surcoût », « 0 € ». */
    montant: string
    sens: "gain" | "perte" | "nul"
    /** « (156 € prévus, contre 8 € aujourd'hui) », ou `null`. */
    precision: string | null
}

/** La fiche de la carte : trois étiquettes au plus, chacune en une ligne. */
export type FicheDeLaCarte = {
    scenario: ScenarioDeLaFiche | null
    /** « HPH et HCH 66 → 61 kVA · HPB et HCB 66 → 62 kVA · formule inchangée » */
    aDemander: string | null
    /** C5 : « Pourquoi 11 kVA », avec le repli servi (D7, A7). */
    pourquoi: { titre: string; texte: string } | null
    composition: TermeAffiche[]
    /**
     * C5 sous le pic (règle du montant C5, DEV-2422) : « D'où vient le
     * montant » en mots, « 62 € HT de plus par an sur l'acheminement, pour 11
     * kVA à formule inchangée. », à la place de la composition (le chiffrage
     * dédié n'a pas de décomposition) ; absent sans montant servi.
     */
    provenance?: string | null
    /** La phrase servie de la formule recalculée, dite à part, ou `null`. */
    formuleRecalculee?: string | null
    /** « Du 18/01/2026 au 25/09/2026 » (économie non chiffrée), ou `null`. */
    periode: string | null
}

/** La ligne en couleur de la carte : une action orange ou un constat gris. */
export type AlerteDeLaCarte = {
    texte: string
    niveau: "action" | "constat"
    /** Le champ que « Corriger » ouvre (action), ou `null`. */
    ancre: AncreAffiner | null
}

export type CarteDeTete = {
    etat: EtatDeCarte
    titre: TitreDeCarte
    /** « Acheminement (TURPE) seul, hors TVA, CTA comprise. … » */
    perimetre: string
    /** La raison de la décision, ou `null` (la ligne se tait). */
    raison: string | null
    aDemander: ValeurADemander[]
    alerte: AlerteDeLaCarte | null
    fiche: FicheDeLaCarte
    action: ActionDeCarte
    secondaire: ActionSecondaire
    /** « Voir le résumé » en lien de niveau 3 (A2), ou `null`. */
    lien: "voir_resume" | null
    /** La période proposée par le moteur, pour « Chiffrer sur cette période ». */
    periode: { date_from: string; date_to: string } | null
    /** Le scénario conseillé quand la carte montre l'autre (`scenario_non_recommande`). */
    scenarioConseille?: "sans" | "avec" | null
}

/** Ce que la carte lit de la paire. */
type ContexteDeCarte = Pick<
    ContexteEntetes,
    "arbitragePertinent" | "retenu" | "conseil" | "sans" | "avec"
>

function fini(valeur: unknown): number | null {
    return typeof valeur === "number" && Number.isFinite(valeur) ? valeur : null
}

/** « 2026-01-18 » → « 18/01/2026 ». */
function dateFr(iso: string | null | undefined): string | null {
    const m = iso ? /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) : null
    return m ? `${m[3]}/${m[2]}/${m[1]}` : null
}

/** Une mesure à une décimale, toujours (R-T4) : « 9,0 », « 104,5 ». */
function mesureFr(valeur: number): string {
    return formateurFr({
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    }).format(valeur)
}

/** Une souscrite ou une valeur à demander, entière (R-T4). */
function entierFr(valeur: number): string {
    return formatNombreFr(Math.round(valeur), 0) ?? String(Math.round(valeur))
}

/** « 1 161 € », « moins de 1 € » : un montant sans signe, porté par un mot. */
function euros(valeur: number): string {
    const absolu = Math.abs(valeur)
    if (absolu > 0 && absolu < 0.5) return "moins de 1\u00a0€"
    return `${nombreEuros(valeur)}\u00a0€`
}

/**
 * La forme nominale d'un montant (D1 b, A3) : « 2 355 € d'économie », « 1 399
 * € de surcoût » ; un terme nul s'écrit « 0 € ».
 */
export function montantNominal(valeur: number): string {
    const sens = sensMontant(valeur)
    if (sens === "nul") return "0\u00a0€"
    return `${nombreEuros(valeur)}\u00a0€ ${sens === "gain" ? "d'économie" : "de surcoût"}`
}

/** Le texte du bouton « Chiffrer sur la période du … au … » (Q26). */
export function libelleChiffrerSurLaPeriode(periode: {
    date_from: string
    date_to: string
}): string {
    const du = dateFr(periode.date_from) ?? periode.date_from
    const au = dateFr(periode.date_to) ?? periode.date_to
    return `Chiffrer sur la période du ${du} au ${au}`
}

/** Le code de la puissance d'une ligne de la demande (`Puissance souscrite HPB`), ou `null`. */
function posteDeLaLigne(ligne: LigneDemande): string | null {
    const m = /^Puissance souscrite (\S+)$/.exec(ligne.libelle)
    return m ? m[1] : null
}

/**
 * Ce qui change dans la demande, regroupé : la formule, puis les postes qui
 * passent d'une même valeur à une même valeur ensemble. `[]` sans demande.
 */
export function valeursADemander(
    etude: OptimTurpeV2Etude | null | undefined
): ValeurADemander[] {
    if (!etude) return []
    const demande = lireDemande(etude)
    if (demande?.etat !== "demande") return []
    const changees = demande.lignes.filter((l) => !l.reference && !l.inchangee)
    const valeurs: ValeurADemander[] = []
    const fta = changees.find(
        (l) => l.libelle === "Formule tarifaire d'acheminement"
    )
    if (fta) {
        const sansMot = (v: string) => v.replace(/^formule /, "")
        valeurs.push({
            libelle: "Formule",
            aujourdhui: fta.aujourdhui ? sansMot(fta.aujourdhui) : null,
            valeur: sansMot(fta.valeur),
        })
    }
    const puissances = changees.filter((l) =>
        l.libelle.startsWith("Puissance souscrite")
    )
    const unique = puissances.find((l) => posteDeLaLigne(l) === null)
    if (unique) {
        valeurs.push({
            libelle: "Puissance souscrite",
            aujourdhui: unique.aujourdhui ?? null,
            valeur: unique.valeur,
        })
        return valeurs
    }
    const tous = demande.lignes.filter((l) => posteDeLaLigne(l) !== null).length
    const groupes = new Map<string, { postes: string[]; ligne: LigneDemande }>()
    for (const ligne of puissances) {
        const poste = posteDeLaLigne(ligne)
        if (!poste) continue
        const cle = `${ligne.aujourdhui ?? ""}|${ligne.valeur}`
        const groupe = groupes.get(cle)
        if (groupe) groupe.postes.push(poste)
        else groupes.set(cle, { postes: [poste], ligne })
    }
    for (const { postes, ligne } of groupes.values()) {
        valeurs.push({
            libelle: nomDesPostes(postes, tous),
            aujourdhui: ligne.aujourdhui ?? null,
            valeur: ligne.valeur,
            postes,
            tous,
        })
    }
    return valeurs
}

const NOMBRE_EN_LETTRES: Record<number, string> = {
    2: "deux",
    3: "trois",
    4: "quatre",
    5: "cinq",
}

/** « HPH et HCH », « les cinq postes » : des postes par leurs codes. */
function nomDesPostes(postes: string[], tous: number): string {
    if (postes.length > 1 && postes.length === tous && NOMBRE_EN_LETTRES[tous])
        return `les ${NOMBRE_EN_LETTRES[tous]} postes`
    return listeEt(postes)
}

/** « 61 et 62 », « 61, 62 et 66 ». */
function listeEt(elements: string[]): string {
    if (elements.length <= 1) return elements.join("")
    return `${elements.slice(0, -1).join(", ")} et ${elements[elements.length - 1]}`
}

/** « 66 kVA » → « 66 » quand l'unité est celle de la valeur demandée. */
function sansUniteCommune(avant: string, apres: string): string {
    const unite = /\s(kVA|kW)$/.exec(apres)?.[1]
    return unite ? avant.replace(new RegExp(`\\s${unite}$`), "") : avant
}

/**
 * « longue utilisation à pointe fixe (HTALU5) » et « courte utilisation à
 * pointe fixe (HTACU5) » → « longue → courte utilisation à pointe fixe (HTALU5
 * → HTACU5) » : le libellé commun dit une fois (contrat §4, A1, A2).
 */
export function formuleAvantApres(avant: string, apres: string): string {
    const lire = (v: string) => {
        const m = /^(.*?)\s*\(([A-Z0-9]+)\)$/.exec(v.trim())
        return m
            ? { libelle: m[1], code: m[2] }
            : { libelle: v.trim(), code: null }
    }
    const a = lire(avant)
    const b = lire(apres)
    const motsA = a.libelle.split(" ")
    const motsB = b.libelle.split(" ")
    let commun = 0
    while (
        commun < motsA.length - 1 &&
        commun < motsB.length - 1 &&
        motsA[motsA.length - 1 - commun] === motsB[motsB.length - 1 - commun]
    )
        commun += 1
    const debutA = motsA.slice(0, motsA.length - commun).join(" ")
    const libelle =
        commun > 0 ? `${debutA} → ${b.libelle}` : `${a.libelle} → ${b.libelle}`
    const codes = a.code && b.code ? ` (${a.code} → ${b.code})` : ""
    return `${libelle}${codes}`
}

/**
 * La ligne « À demander » (contrat §4, C10) : les puissances, puis la formule
 * (« formule inchangée » quand seules les puissances changent) ; en C5,
 * « puissance souscrite 6 → 11 kVA ». `null` sans demande.
 */
export function ligneADemander(valeurs: ValeurADemander[]): string | null {
    if (valeurs.length === 0) return null
    const morceaux: string[] = []
    let formule: string | null = null
    for (const v of valeurs) {
        if (v.libelle === "Formule") {
            formule = v.aujourdhui
                ? `formule ${formuleAvantApres(v.aujourdhui, v.valeur)}`
                : `formule ${v.valeur}`
            continue
        }
        const nom =
            v.libelle === "Puissance souscrite"
                ? "puissance souscrite"
                : v.libelle
        morceaux.push(
            v.aujourdhui
                ? `${nom} ${sansUniteCommune(v.aujourdhui, v.valeur)} → ${v.valeur}`
                : `${nom} ${v.valeur}`
        )
    }
    morceaux.push(formule ?? "formule inchangée")
    return morceaux.join(" · ")
}

/**
 * La même demande, pour le résumé du client (Q2) : sans code, les puissances
 * puis la formule en clair (« les cinq postes 90 → 62 kW, la formule courte
 * utilisation à pointe fixe »).
 */
export function demandeSansCode(valeurs: ValeurADemander[]): string | null {
    if (valeurs.length === 0) return null
    const morceaux: string[] = []
    let formule: string | null = null
    for (const v of valeurs) {
        if (v.libelle === "Formule") {
            formule = `la formule ${v.valeur.replace(/\s*\([A-Z0-9]+\)$/, "")}`
            continue
        }
        const avant = v.aujourdhui
            ? `${sansUniteCommune(v.aujourdhui, v.valeur)} → `
            : ""
        morceaux.push(
            v.libelle === "Puissance souscrite"
                ? `${avant}${v.valeur}`
                : `${v.libelle} ${avant}${v.valeur}`
        )
    }
    if (formule) morceaux.push(formule)
    else morceaux.push("formule inchangée")
    return morceaux.join(", ")
}

/** Les puissances souscrites distinctes d'une structure, croissantes. */
function puissancesDe(
    structure:
        | {
              puissance_souscrite_soutirage?: {
                  puissance?: number | null
                  denivele?: Record<string, number | null | undefined> | null
              } | null
          }
        | null
        | undefined
): number[] {
    const p = structure?.puissance_souscrite_soutirage
    const valeurs = [
        fini(p?.puissance),
        ...Object.values(p?.denivele ?? {}).map(fini),
    ].filter((v): v is number => v !== null)
    return [...new Set(valeurs)].sort((a, b) => a - b)
}

/** Le nombre de postes d'une structure (4 en C4, 5 en C2), ou `null`. */
function nombreDePostes(
    structure: OptimTurpeV2Etude["meilleure"] | null | undefined
): number | null {
    const denivele =
        structure?.structure_tarifaire?.puissance_souscrite_soutirage?.denivele
    const n = Object.values(denivele ?? {}).filter(
        (v) => fini(v) !== null
    ).length
    return n > 1 ? n : null
}

/** La formule d'une structure (code), ou `null`. */
function formuleDe(
    structure: OptimTurpeV2Etude["meilleure"] | null | undefined
): string | null {
    return (
        structure?.structure_tarifaire?.formule_tarifaire_acheminement?.fta ??
        null
    )
}

/** Formule et puissances de l'étude saisie égales au contrat (A3) ? */
function structureIdentiqueAuContrat(etude: OptimTurpeV2Etude): boolean {
    const avant = etude.actuelle?.structure_tarifaire
    const apres = etude.meilleure?.structure_tarifaire
    if (!avant || !apres) return false
    if (formuleDe(etude.actuelle) !== formuleDe(etude.meilleure)) return false
    const pa = avant.puissance_souscrite_soutirage
    const pb = apres.puissance_souscrite_soutirage
    if (fini(pa?.puissance) !== null || fini(pb?.puissance) !== null)
        return fini(pa?.puissance) === fini(pb?.puissance)
    const cles = new Set([
        ...Object.keys(pa?.denivele ?? {}),
        ...Object.keys(pb?.denivele ?? {}),
    ])
    if (cles.size === 0) return false
    for (const cle of cles) {
        const a = fini(
            (pa?.denivele as Record<string, unknown> | undefined)?.[cle]
        )
        const b = fini(
            (pb?.denivele as Record<string, unknown> | undefined)?.[cle]
        )
        if (a === null || b === null || a !== b) return false
    }
    return true
}

/** La plus haute puissance mesurée servie, ou `null`. */
function picMesure(etude: OptimTurpeV2Etude): number | null {
    const pics = Object.values(etude.pointes_mesurees_kw ?? {})
        .map(fini)
        .filter((v): v is number => v !== null)
    return pics.length > 0 ? Math.max(...pics) : null
}

/** « 61 et 62 kVA sur les quatre postes », « 62 kW sur les cinq postes ». */
function puissancesSurLesPostes(etude: OptimTurpeV2Etude): string | null {
    const apres = puissancesDe(etude.meilleure?.structure_tarifaire)
    if (apres.length === 0) return null
    const unite = getUnitePuissance(etude)
    const n = nombreDePostes(etude.meilleure)
    const nombres = listeEt(apres.map(entierFr))
    return n && NOMBRE_EN_LETTRES[n]
        ? `${nombres}\u00a0${unite} sur les ${NOMBRE_EN_LETTRES[n]} postes`
        : `${nombres}\u00a0${unite}`
}

/**
 * La raison d'une économie (contrat §4, cinq formes, C3), composée des valeurs
 * servies ; `null` quand aucune forme ne se compose (la ligne se tait, jamais
 * « L'essentiel vient de … »).
 */
export function raisonDuConseil(
    etude: OptimTurpeV2Etude | null | undefined,
    ctx: ContexteDeCarte = {}
): string | null {
    if (!etude) return null
    const unite = getUnitePuissance(etude)
    const avant = puissancesDe(etude.actuelle?.structure_tarifaire)
    const apres = puissancesDe(etude.meilleure?.structure_tarifaire)
    const chiffre = lireChiffre(etude)
    const parPeriode = surLaPeriode(chiffre.periode.jours, chiffre.periode)
    // Formule seule : les puissances ne changent pas.
    const memesPuissances =
        avant.length > 0 &&
        avant.length === apres.length &&
        avant.every((v, i) => v === apres[i])
    const fta = formuleDe(etude.meilleure)
    if (memesPuissances && fta && fta !== formuleDe(etude.actuelle)) {
        const libelle = LIBELLE_FORMULE_TARIFAIRE[fta]
        return libelle
            ? `La formule ${libelle} coûte moins cher pour ce profil de consommation, à puissances inchangées.`
            : null
    }
    if (avant.length === 0 || apres.length === 0) return null
    const souscrite = Math.max(...avant)
    const conseillee = Math.max(...apres)
    // Hausse pour éviter des dépassements.
    if (conseillee > souscrite) {
        const pic = picMesure(etude)
        const evites = lireChiffre(etude).decomposition.depassementsEur
        if (pic === null || evites === null || evites <= 0) return null
        return `Le site atteint ${mesureFr(pic)}\u00a0${unite} pour ${entierFr(souscrite)}\u00a0${unite} souscrits : ces dépassements coûtent environ ${euros(evites)} HT ${parPeriode}. Monter à ${entierFr(conseillee)}\u00a0${unite} les évite.`
    }
    if (conseillee === souscrite) return null
    // Baisse avec quelques dépassements assumés (C4, C2).
    if (
        ctx.arbitragePertinent &&
        ctx.retenu === "avec" &&
        ctx.conseil === "avec"
    ) {
        const arbitrage = lireArbitrageServi(ctx.sans ?? null, ctx.avec ?? null)
        const postes = puissancesSurLesPostes(etude)
        if (!postes) return null
        const duree = arbitrage?.dureeDepassementH ?? null
        if (duree !== null && duree > 0) {
            const heures = Math.max(1, Math.round(duree))
            return `Avec ${postes}, le site dépassera sa puissance environ ${heures}\u00a0heure${heures >= 2 ? "s" : ""} ${parPeriode}. Ces dépassements coûtent moins que ce que la baisse de puissance fait gagner.`
        }
        const prevus = arbitrage?.depassementsAssumes ?? null
        if (prevus !== null && prevus > 0)
            return `Avec ${postes}, les dépassements prévus (${euros(prevus)} HT ${parPeriode}) coûtent moins que ce que la baisse de puissance fait gagner.`
        return null
    }
    // Baisse sans dépassement : la puissance atteinte reste sous la conseillée.
    const pic = picMesure(etude)
    if (pic === null || pic > conseillee) return null
    const sansDepassement = getCmdpsEstimeeEur(etude) === 0
    return `Puissance maximale atteinte : ${mesureFr(pic)}\u00a0${unite} pour ${entierFr(souscrite)}\u00a0${unite} souscrits ; ${entierFr(conseillee)}\u00a0${unite} suffisent${sansDepassement ? ", sans dépassement sur la période étudiée" : ""}.`
}

/**
 * La forme verbale d'un écart face au contrat (D1 b) : « ferait gagner 21 €
 * HT par an », « coûterait 5 € HT de plus par an ».
 */
function formeVerbale(montant: number, unite: string): string {
    return montant >= 0
        ? `ferait gagner ${euros(montant)} HT ${unite}`
        : `coûterait ${euros(montant)} HT de plus ${unite}`
}

/** La réserve C5 de Q22 servie, ou `null`. */
function reserveC5(etude: OptimTurpeV2Etude) {
    if (etude.compteur?.segment !== "C5") return null
    return (
        (etude.reserves ?? []).find((r) => r?.code === CODE_RESERVE_C5_PIC) ??
        null
    )
}

/**
 * DEV-2423 (DEV-2421 point 1) : la phrase de l'entête de la carte
 * « Puissances par poste » sur un C5 avec la réserve servie, « Le compteur a
 * atteint 9,0 kVA pour 6 kVA souscrits. » (mots de F1 et de US12, sans
 * montant ni scénario). `null` hors C5, sans réserve ou sans ses deux
 * valeurs : l'entête se tait plutôt que d'inventer (jamais un 0). Vu en
 * production le 18/10 sur le site C5 de référence : l'entête C4 (« Couvrir
 * les dépassements coûte plus cher… ») fuyait sur le C5.
 */
export function phraseEnteteGraphiqueC5(
    etude: OptimTurpeV2Etude | null | undefined
): string | null {
    if (!etude) return null
    const reserve = reserveC5(etude)
    const valeurs = (reserve?.valeurs ?? {}) as Record<string, unknown>
    const pic = fini(valeurs.pic_kva)
    const souscrite = fini(valeurs.souscrite_kva)
    if (pic === null || souscrite === null) return null
    return `Le compteur a atteint ${mesureFr(pic)}\u00a0kVA pour ${entierFr(souscrite)}\u00a0kVA souscrits.`
}

/**
 * Le verdict et la raison C5 (A1, D9) : le libellé SERVI de la réserve, coupé
 * à sa première phrase (« Risque de disjonction : le compteur a atteint 9,0
 * kVA pour 6 kVA souscrits »), le reste devient la raison (« Passer à 11 kVA
 * supprime ce risque, pour 62 € HT de plus par an sur l'acheminement. »).
 * Un libellé d'avant le lot 4a reste entier en titre.
 */
function verdictC5(libelle: string): { titre: string; raison: string | null } {
    const propre = libelle.trim()
    const m = /^(Risque de disjonction : .+?souscrits)\.\s*(.*)$/s.exec(propre)
    if (!m) return { titre: propre, raison: null }
    return { titre: m[1], raison: m[2].trim() || null }
}

/**
 * « D'où vient le montant » du C5 sous le pic (contrat v1.3, règle du montant
 * C5, décision du 19/10 n° 1) : le montant du chiffrage dédié de la puissance
 * adaptée à formule actuelle, sa provenance en mots, puis la phrase servie de
 * la formule recalculée. `null` sans montant servi : la ligne se tait, jamais
 * un 0.
 */
function provenanceC5(
    etude: OptimTurpeV2Etude
): { provenance: string; formuleRecalculee: string | null } | null {
    const montant = montantC5(etude)
    if (!montant || montant.surcoutEur === null) return null
    return {
        provenance: `${montant.environ ? "environ " : ""}${euros(montant.surcoutEur)} HT de plus par an sur l'acheminement, pour ${entierFr(montant.puissanceKva)}\u00a0kVA à formule inchangée.`,
        formuleRecalculee: montant.phraseFormuleRecalculee,
    }
}

/**
 * La raison C5 servie, accordée au montant lu (une seule conclusion, US12
 * CA1) :
 * - la formule recalculée se dit à part, sous « D'où vient le montant », quand
 *   le montant de la puissance adaptée y est dit ;
 * - sans montant lu (une `cause` servie sur le chiffrage dédié), la raison
 *   revient à la forme sans montant du moteur, « Passer à 11 kVA supprime ce
 *   risque. » (`pic_c5.py`, `LIBELLE_ACTION`) : aucune surface ne dit un
 *   montant que la provenance et « Pourquoi cette puissance » taisent.
 */
function raisonC5(
    raison: string | null,
    formuleRecalculee: string | null,
    etude: OptimTurpeV2Etude
): string | null {
    if (!raison) return null
    if (formuleRecalculee)
        return raison.replace(formuleRecalculee, "").trim() || null
    const montant = montantC5(etude)
    if (montant && montant.surcoutEur === null && raison.includes("€")) {
        const sansMontant = /^(Passer à .+? kVA supprime ce risque)\b/.exec(
            raison
        )
        if (sansMontant) return `${sansMontant[1]}.`
    }
    return raison
}

/**
 * La fiche « Pourquoi 11 kVA » (A1, D7, A7) : pic, marge appliquée d'office,
 * arrondi au pas appliqué, puis le repli servi par la demande. `null` sans
 * puissance adaptée servie.
 */
function pourquoiC5(
    etude: OptimTurpeV2Etude
): { titre: string; texte: string } | null {
    const reserve = reserveC5(etude)
    const valeurs = (reserve?.valeurs ?? {}) as Record<string, unknown>
    const adaptee = fini(valeurs.puissance_adaptee_kva)
    const pic = fini(valeurs.pic_kva) ?? fini(etude.pmax_mesuree?.valeur)
    if (adaptee === null || pic === null) return null
    const marge = fini(etude.pmax_mesuree?.marge_appliquee)
    const pas = fini(etude.branchement?.pas_kva)
    const morceaux = [
        `pic de ${mesureFr(pic)}\u00a0kVA`,
        marge !== null && marge > 0
            ? `plus ${formatNombreFr(Math.round(marge * 1000) / 10, 1)}\u00a0% de marge de sécurité`
            : null,
        pas === 3
            ? "arrondi au palier de 3\u00a0kVA supérieur"
            : pas === 1
              ? "arrondi au kVA supérieur"
              : null,
    ].filter(Boolean)
    const repli = etude.demande?.repli_palier_usuel?.libelle?.trim()
    return {
        titre: `Pourquoi ${entierFr(adaptee)}\u00a0kVA`,
        texte: `${morceaux.join(", ")}.${repli ? ` ${repli}` : ""}`,
    }
}

/**
 * La raison d'une étude saisie identique au contrat, en C5 avec le risque
 * servi (A3, D10, C8) : rien à demander, mais le compteur peut disjoncter et
 * l'étude recommandée existe.
 */
function raisonVosPuissancesIdentiques(etude: OptimTurpeV2Etude): string {
    const reserve = reserveC5(etude)
    const valeurs = (reserve?.valeurs ?? {}) as Record<string, unknown>
    const pic = fini(valeurs.pic_kva)
    const souscrite = fini(valeurs.souscrite_kva)
    if (pic === null || souscrite === null)
        return "Rien à demander au fournisseur."
    const adaptee = fini(valeurs.puissance_adaptee_kva)
    // « 11 kVA en courte utilisation » (A3) : la formule sans sa précision.
    const formule =
        typeof valeurs.formule_adaptee === "string"
            ? (LIBELLE_FORMULE_TARIFAIRE[valeurs.formule_adaptee]?.split(
                  " à "
              )[0] ?? null)
            : null
    const changeDeFormule =
        typeof valeurs.formule_adaptee === "string" &&
        valeurs.formule_adaptee !== valeurs.formule_actuelle
    // Le seul montant du chiffrage dédié (DEV-2422) ; une cause servie le tait.
    const surcout = montantC5(etude)?.surcoutEur ?? null
    const recommande =
        adaptee !== null
            ? ` L'étude recommandée propose ${entierFr(adaptee)}\u00a0kVA${
                  changeDeFormule && formule ? ` en ${formule}` : ""
              }${surcout !== null && surcout > 0 ? `, pour ${euros(surcout)} HT de plus par an` : ""}.`
            : ""
    return `Rien à demander au fournisseur. Attention : le compteur a atteint ${mesureFr(pic)}\u00a0kVA pour ${entierFr(souscrite)}\u00a0kVA souscrits, il peut disjoncter.${recommande}`
}

/** Les termes de « D'où vient le montant », en forme nominale (D1 b, A3). */
export function compositionAffichee(
    etude: OptimTurpeV2Etude | null | undefined
): TermeAffiche[] {
    if (!etude) return []
    const c5 = etude.compteur?.segment === "C5"
    const termes = compositionDuMontant(etude)
    const prevus = getCmdpsEstimeeEur(etude)
    const aujourdhui = fini(etude.actuelle?.couts?.turpeCMDPS)
    // Contrat v1.2 §4 : part puissance, part énergie, CTA, puis dépassements.
    const ordonnes = [
        ...termes.filter((t) => !t.nom.startsWith("dépassements")),
        ...termes.filter((t) => t.nom.startsWith("dépassements")),
    ]
    return ordonnes
        .filter((t) => !(c5 && t.nom.startsWith("dépassements")))
        .map((t) => {
            const depassements = t.nom.startsWith("dépassements")
            return {
                nom: depassements
                    ? "dépassements"
                    : t.nom.replace(/ \((CSF|CSV)\)$/, ""),
                montant: montantNominal(t.eur),
                sens: sensMontant(t.eur),
                // Les deux termes servis, seulement quand ils font le terme
                // affiché (règle de somme, F8) ; sinon la parenthèse se tait.
                precision:
                    depassements &&
                    prevus !== null &&
                    aujourdhui !== null &&
                    Math.abs(aujourdhui - prevus - t.eur) < 1
                        ? `(${nombreEuros(prevus)}\u00a0€ prévus, contre ${nombreEuros(aujourdhui)}\u00a0€ aujourd'hui)`
                        : null,
            }
        })
}

/** La composition en une ligne de texte (lecture d'écran, résumé, tests). */
export function texteDeLaComposition(termes: TermeAffiche[]): string | null {
    if (termes.length === 0) return null
    return termes
        .map(
            (t) =>
                `${t.nom} : ${t.montant}${t.precision ? ` ${t.precision}` : ""}`
        )
        .join(" · ")
}

/** La ligne « Scénario » de la fiche (A2), ou `null` sans arbitrage pertinent. */
function scenarioDeLaFiche(
    etude: OptimTurpeV2Etude,
    ctx: ContexteDeCarte
): ScenarioDeLaFiche | null {
    if (!ctx.arbitragePertinent || !ctx.retenu) return null
    if (etude.compteur?.segment === "C5") return null
    const nom = LIBELLE_SCENARIO_ARBITRAGE[ctx.retenu]
    const recommande = ctx.conseil === ctx.retenu
    const chiffre = lireChiffre(etude)
    const parPeriode = surLaPeriode(chiffre.periode.jours, chiffre.periode)
    let detail: string | null = null
    if (ctx.retenu === "avec") {
        const estimes =
            lireArbitrageServi(ctx.sans ?? null, ctx.avec ?? null)
                ?.depassementsAssumes ?? null
        if (estimes !== null && estimes > 0)
            detail = `${euros(estimes)} HT ${parPeriode} de dépassements, déjà déduits.`
    } else {
        const postes = puissancesDe(etude.meilleure?.structure_tarifaire)
        if (getCmdpsEstimeeEur(etude) === 0 && postes.length > 0)
            detail = `aucun dépassement prévu avec ${listeEt(postes.map(entierFr))}\u00a0${getUnitePuissance(etude)}.`
    }
    return { nom, recommande, detail }
}

function titre(
    texte: string,
    montantEur: number | null = null,
    unite: string | null = null,
    soitEnviron: string | null = null
): TitreDeCarte {
    return { texte, montantEur, unite, soitEnviron }
}

/** « Économie estimée : la période … » → « La période … ». */
function phraseSansVerdict(phrase: string): string {
    const reste = phrase.replace(/^[^:]*:\s*/, "").trim()
    return reste ? `${reste.charAt(0).toUpperCase()}${reste.slice(1)}` : phrase
}

/** D27 : la condition de l'économie, qui ouvre la raison. */
const CONDITION_D27 = "En assumant les dépassements."

/** Le gabarit vide d'une carte, que chaque état complète. */
function carteVide(): Omit<CarteDeTete, "etat" | "titre"> {
    return {
        perimetre: LIGNE_DE_PERIMETRE,
        raison: null,
        aDemander: [],
        alerte: null,
        fiche: {
            scenario: null,
            aDemander: null,
            pourquoi: null,
            composition: [],
            provenance: null,
            formuleRecalculee: null,
            periode: null,
        },
        action: null,
        secondaire: null,
        lien: null,
        periode: null,
    }
}

/**
 * La carte de tête de l'étude affichée, ou `null` (refus, aucune étude).
 * `ctx` porte la paire (scénario affiché, conseil établi).
 */
export function lireCarteDeTete(
    etude: OptimTurpeV2Etude | null | undefined,
    ctx: ContexteDeCarte = {}
): CarteDeTete | null {
    const carte = lireCarteSansAlerte(etude, ctx)
    if (!carte || !etude) return null
    return { ...carte, alerte: alerteDeLaCarte(etude, carte) }
}

/** La carte d'une étude non chiffrée (contrat §4, maquette 2b état 6). */
function carteNonChiffree(etude: OptimTurpeV2Etude): CarteDeTete {
    const chiffre = lireChiffre(etude)
    const cause = chiffre.montantNonServiCause
    const phraseLongue =
        (cause ? LIBELLE_MONTANT_NON_SERVI[cause] : undefined) ??
        (chiffre.etatCause
            ? (() => {
                  const c = LIBELLE_ETAT_CAUSE[chiffre.etatCause]
                  return c
                      ? `${LIBELLE_ETAT.montant_non_servi} : ${c.charAt(0).toLowerCase()}${c.slice(1)}`
                      : null
              })()
            : null) ??
        ECONOMIE_NON_CHIFFREE_SANS_CAUSE
    const texte =
        (cause ? VERDICT_NON_CHIFFRE_COURT[cause] : undefined) ??
        (phraseLongue.startsWith(`${LIBELLE_ETAT.montant_non_servi} : `)
            ? LIBELLE_ETAT.montant_non_servi
            : phraseLongue)
    const causePhrase =
        texte === phraseLongue ? null : phraseSansVerdict(phraseLongue)
    const jours = libelleJoursMesures(etude.qualite_donnees)
    const parJours = jours
        ? `${jours.charAt(0).toUpperCase()}${jours.slice(1)} : l'étude ne conclut ni sur le montant ni sur les puissances.`
        : null
    const deMesure = cause ? CAUSES_NON_CHIFFRE_DE_MESURE.includes(cause) : true
    const meilleure = getMeilleurePeriode(etude)
    const periode =
        meilleure?.date_from && meilleure?.date_to
            ? { date_from: meilleure.date_from, date_to: meilleure.date_to }
            : null
    const vide = carteVide()
    return {
        ...vide,
        etat: "non_chiffree",
        titre: titre(texte),
        raison: deMesure
            ? (parJours ?? causePhrase)
            : (causePhrase ?? parJours),
        fiche: {
            ...vide.fiche,
            periode: periode
                ? `Du ${dateFr(periode.date_from)} au ${dateFr(periode.date_to)}`
                : null,
        },
        action: periode ? "chiffrer_periode" : "modifier_hypotheses",
        secondaire: "telecharger",
        periode,
    }
}

function lireCarteSansAlerte(
    etude: OptimTurpeV2Etude | null | undefined,
    ctx: ContexteDeCarte
): CarteDeTete | null {
    if (!etude) return null
    const etatParcours = etatDuParcours({ etude })
    if (etatParcours === "refus" || etatParcours === "vide") return null
    if (etatParcours === "montant_non_servi") return carteNonChiffree(etude)
    const chiffre = lireChiffre(etude)
    const tete = montantDeTete(chiffre)
    const vide = carteVide()
    const aDemander = valeursADemander(etude)
    const avecDemande = aDemander.length > 0
    const fiche = (extra: Partial<FicheDeLaCarte> = {}): FicheDeLaCarte => ({
        ...vide.fiche,
        aDemander: ligneADemander(aDemander),
        composition: compositionAffichee(etude),
        ...extra,
    })

    // Étude saisie (« Vos puissances ») : face au contrat, ce n'est pas le
    // conseil (A3, D10, D17).
    if (etatParcours === "scenario_saisi") {
        if (structureIdentiqueAuContrat(etude)) {
            return {
                ...vide,
                etat: "vos_puissances_identiques",
                titre: titre(
                    "Vos puissances : identiques au contrat actuel (0\u00a0€ d'écart)"
                ),
                raison: raisonVosPuissancesIdentiques(etude),
                action: "voir_recommandation",
                secondaire: "voir_resume",
            }
        }
        const seuil = fini(chiffre.seuilEur)
        const sousLeSeuil =
            tete !== null && seuil !== null && Math.abs(tete.montantEur) < seuil
        return {
            ...vide,
            etat: "vos_puissances",
            titre: tete
                ? titre(
                      "Vos puissances :",
                      tete.montantEur,
                      tete.unite,
                      tete.soitEnviron
                  )
                : titre("Vos puissances"),
            raison: sousLeSeuil
                ? `Sous le seuil de ${seuil}\u00a0€. Ce n'est pas la recommandation.`
                : "Face au contrat actuel. Ce n'est pas la recommandation.",
            aDemander,
            fiche: fiche(),
            action: "voir_recommandation",
            secondaire: avecDemande ? "preparer_demande" : "voir_resume",
            lien: avecDemande ? "voir_resume" : null,
        }
    }

    // Le scénario affiché n'est pas le conseil établi (R2) : le nom court et
    // son montant, le conseil et son montant dans la raison (A2, D17).
    if (
        ctx.arbitragePertinent &&
        ctx.retenu &&
        ctx.conseil &&
        ctx.retenu !== ctx.conseil
    ) {
        const conseille = ctx.conseil === "sans" ? ctx.sans : ctx.avec
        const teteConseil = conseille
            ? montantDeTete(lireChiffre(conseille))
            : null
        return {
            ...vide,
            etat: "scenario_non_recommande",
            titre: tete
                ? titre(
                      `${NOM_COURT_SCENARIO[ctx.retenu]} :`,
                      tete.montantEur,
                      tete.unite,
                      tete.soitEnviron
                  )
                : titre(LIBELLE_SCENARIO_ARBITRAGE[ctx.retenu]),
            raison: `${LIBELLE_SCENARIO_ARBITRAGE[ctx.retenu]} n'est pas la recommandation.${
                teteConseil
                    ? ` ${LIBELLE_SCENARIO_ARBITRAGE[ctx.conseil]} : ${formatMontantSigne(teteConseil.montantEur)} HT ${teteConseil.unite}.`
                    : ""
            }`,
            aDemander,
            fiche: fiche(),
            action: "voir_recommandation",
            secondaire: avecDemande ? "preparer_demande" : "voir_resume",
            lien: avecDemande ? "voir_resume" : null,
            scenarioConseille: ctx.conseil,
        }
    }

    if (etatParcours === "economie") {
        const gainDepassements = chiffre.etat === "gain_par_depassements_evites"
        return {
            ...vide,
            etat: gainDepassements
                ? "gain_par_depassements_evites"
                : "economie",
            titre: titre(
                `${LIBELLE_ETAT.economie} :`,
                tete?.montantEur ?? null,
                tete?.unite ?? null,
                tete?.soitEnviron ?? null
            ),
            raison: raisonDuConseil(etude, ctx),
            aDemander,
            fiche: fiche({ scenario: scenarioDeLaFiche(etude, ctx) }),
            action: avecDemande ? "preparer_demande" : "voir_resume",
            secondaire: "telecharger",
        }
    }

    // D27 : couvrir coûte plus cher qu'aujourd'hui, assumer rapporte.
    const titreD27 = lireTitreDuResultat(
        etude,
        ctx.sans ?? null,
        ctx.avec ?? null
    )
    if (titreD27.forme === "economie_possible") {
        const unite = surLaPeriode(titreD27.jours, titreD27.dates)
        const annuel =
            unite === "par an" || !ctx.avec
                ? null
                : fini(lireChiffre(ctx.avec).equivalentAnnuel?.nette_eur)
        return {
            ...vide,
            etat: "economie",
            titre: titre(
                `${LIBELLE_ETAT.economie} :`,
                titreD27.possibleEur,
                unite,
                annuel === null
                    ? null
                    : `soit environ ${formatMontantSigne(annuel, "HT")} par an`
            ),
            raison: `${CONDITION_D27} ${phrasePrixDeLaCouverture(titreD27)}`,
            fiche: fiche({ scenario: scenarioDeLaFiche(etude, ctx) }),
            aDemander,
            action: avecDemande ? "preparer_demande" : "voir_resume",
            secondaire: "telecharger",
        }
    }

    // C5 au-dessus de sa puissance (A1, D9) : la réserve lue avant l'état ;
    // verdict sans montant, raison, demande servie, « Pourquoi 11 kVA ».
    const libelleC5 = reserveTitreC5(etude)
    if (libelleC5) {
        const { titre: texte, raison } = verdictC5(libelleC5)
        const montant = provenanceC5(etude)
        return {
            ...vide,
            etat: "reserve_c5",
            titre: titre(texte),
            // La formule recalculée se dit à part, sous « D'où vient le
            // montant », quand le montant de la puissance adaptée y est dit.
            raison: raisonC5(raison, montant?.formuleRecalculee ?? null, etude),
            aDemander,
            // Aucun montant emprunté à une autre combinaison : la composition
            // de l'économie servie (celle de la combinaison retenue par
            // l'optimisation) ne se dit pas sous la réserve C5.
            fiche: fiche({
                pourquoi: pourquoiC5(etude),
                composition: [],
                provenance: montant?.provenance ?? null,
                formuleRecalculee: montant?.formuleRecalculee ?? null,
            }),
            action: avecDemande ? "preparer_demande" : "voir_resume",
            secondaire: "telecharger",
        }
    }

    const rien = titre(LIBELLE_ETAT.deja_optimale)
    const unite = tete?.unite ?? "par an"
    if (chiffre.etat === "surcout") {
        return {
            ...vide,
            etat: "surcout",
            titre: rien,
            raison: tete
                ? `La combinaison chiffrée coûterait ${euros(tete.montantEur)} HT de plus ${unite} que le contrat actuel.`
                : null,
            action: "voir_resume",
            secondaire: "telecharger",
        }
    }
    if (chiffre.etat === "deja_optimale") {
        const option =
            lireStatutStructure(etude) === "option_sous_le_seuil"
                ? lireOptionSousLeSeuil(etude)
                : null
        const seuil = fini(chiffre.seuilEur)
        let raison: string | null =
            "La formule et les puissances actuelles restent les moins chères sur la période."
        if (option) {
            raison = !tete
                ? null
                : tete.montantEur >= 0
                  ? `L'option chiffrée ${formeVerbale(tete.montantEur, unite)}${
                        seuil !== null
                            ? `, sous le seuil de ${seuil}\u00a0€`
                            : ""
                    } : aucune démarche nécessaire.`
                  : `L'option chiffrée ${formeVerbale(tete.montantEur, unite)} : garder le contrat actuel.`
        }
        return {
            ...vide,
            etat: option ? "option_sous_le_seuil" : "rien_a_changer",
            titre: rien,
            raison,
            action: "voir_resume",
            secondaire: "telecharger",
        }
    }
    return {
        ...vide,
        etat: "inconnu",
        titre: titre(
            chiffre.etat ? LIBELLE_ETAT[chiffre.etat] : "Résultat de l'étude"
        ),
    }
}

/** La carte propose-t-elle une démarche (« Préparer la demande ») ? */
function carteAvecDemarche(carte: CarteDeTete): boolean {
    return (
        carte.action === "preparer_demande" ||
        carte.secondaire === "preparer_demande"
    )
}

/** Les états sans démarche où le constat du raccordement se lit en gris. */
const ETATS_DU_CONSTAT: EtatDeCarte[] = [
    "rien_a_changer",
    "option_sous_le_seuil",
    "surcout",
]

/**
 * La ligne en couleur de la carte (R-T6, R-T7, A1) : le raccordement d'abord,
 * au format court, quand la carte propose une démarche ; sinon la première
 * action avant d'envoyer ; un constat gris (texte complet servi, sans chiffre
 * de repli) sur « Rien à changer » ; rien sur une étude non chiffrée ni sur le
 * risque C5 (le risque est le verdict).
 */
export function alerteDeLaCarte(
    etude: OptimTurpeV2Etude,
    carte: Pick<CarteDeTete, "etat" | "action" | "secondaire">
): AlerteDeLaCarte | null {
    if (carte.etat === "non_chiffree" || carte.etat === "reserve_c5")
        return null
    const raccordement = texteDuRaccordement(etude)
    if (carteAvecDemarche(carte as CarteDeTete)) {
        if (raccordement?.nature === "action")
            return {
                texte: raccordement.court,
                niveau: "action",
                ancre: "raccordement",
            }
        const point = pointsDActions(etude).find(
            (p) => p.ancre !== "raccordement"
        )
        return point
            ? { texte: point.court, niveau: "action", ancre: point.ancre }
            : null
    }
    if (
        ETATS_DU_CONSTAT.includes(carte.etat) &&
        raccordement?.nature === "constat" &&
        (raccordement.forme === "ecartee" ||
            raccordement.forme === "appliquee-atteinte") &&
        raccordement.complet
    )
        return { texte: raccordement.complet, niveau: "constat", ancre: null }
    return null
}

/** « +2 500 € HT par an » ou « +2 300 € HT sur la période du … au … ». */
function montantEnTexte(t: TitreDeCarte): string | null {
    if (t.montantEur === null) return null
    return [formatMontantSigne(t.montantEur, "HT"), t.unite]
        .filter(Boolean)
        .join(" ")
}

/** Le titre de la carte, en une ligne de texte (lecture d'écran, tests). */
export function texteDuTitre(t: TitreDeCarte): string {
    const montant = montantEnTexte(t)
    return [t.texte, montant, t.soitEnviron ? `, ${t.soitEnviron}` : null]
        .filter(Boolean)
        .join(" ")
        .replace(" ,", ",")
}

// =============================================================================
// LE RÉSUMÉ POUR LE CLIENT (contrat §7.5, maquette 2c) : composé à partir de
// la carte affichée, impersonnel, en marque blanche, sans code Enedis (Q2) ;
// les montants en mots, sans signe ni couleur (D1 b).
// =============================================================================

/** Le résumé mis en forme : verdict en gras, ouverture, puces, périmètre. */
export type ResumeDeLaCarte = {
    verdict: string
    ouverture: string | null
    puces: string[]
    perimetre: string
}

const ETUDE_DU_COMPTEUR =
    "L'étude de la part acheminement (TURPE) de votre compteur"

/** « +72 € HT » → « 72 € HT d'économie » ; « −36 € HT » → « 36 € HT de surcoût ». */
function enMots(texte: string): string {
    return texte.replace(
        /([+−])(\d[\d\u202f\u00a0 ]*?)[\s\u00a0]€([\s\u00a0]HT)?/g,
        (_m, signe: string, nombre: string, ht: string | undefined) =>
            `${nombre}\u00a0€${ht ? " HT" : ""} ${signe === "+" ? "d'économie" : "de surcoût"}`
    )
}

/** « Avec 62 kW … » → « avec 62 kW … » : la raison après « Pourquoi : ». */
function minusculeInitiale(texte: string): string {
    return texte ? `${texte.charAt(0).toLowerCase()}${texte.slice(1)}` : texte
}

/** Le verdict d'un montant, en mots et sans signe (« 1 161 € HT par an »). */
function verdictEnMots(t: TitreDeCarte): string | null {
    if (t.montantEur === null) return null
    const sens = sensMontant(t.montantEur)
    const mot =
        sens === "gain" ? " d'économie" : sens === "perte" ? " de surcoût" : ""
    return `${euros(t.montantEur)} HT${mot} ${t.unite ?? ""}`.trim()
}

/**
 * Le résumé de la carte, mis en forme, ou `null` (carte absente, état
 * inconnu, étude non chiffrée : rien n'y est affirmé).
 */
export function resumeEnBlocs(
    carte: CarteDeTete | null | undefined
): ResumeDeLaCarte | null {
    if (!carte || carte.etat === "inconnu" || carte.etat === "non_chiffree")
        return null
    const puces: string[] = []
    const pourquoi = (raison: string | null) => {
        if (raison)
            puces.push(`Pourquoi : ${minusculeInitiale(enMots(raison))}`)
    }
    const demande = demandeSansCode(carte.aDemander)
    let verdict = carte.titre.texte
    let ouverture: string | null = null
    switch (carte.etat) {
        case "economie":
        case "gain_par_depassements_evites": {
            const montant =
                `${euros(carte.titre.montantEur ?? 0)} HT ${carte.titre.unite ?? ""}`.trim()
            const environ = carte.titre.soitEnviron
                ? `, ${carte.titre.soitEnviron.replace("+", "")}`
                : ""
            verdict = `Économie estimée : ${montant}${environ}`
            const d27 = carte.raison?.startsWith(`${CONDITION_D27} `) === true
            ouverture = `${ETUDE_DU_COMPTEUR} fait apparaître une économie estimée de ${montant}${d27 ? " en assumant les dépassements" : ""}${environ}.`
            pourquoi(carte.raison)
            if (demande) puces.push(`À demander au fournisseur : ${demande}.`)
            break
        }
        case "reserve_c5": {
            const constat = /^Risque de disjonction : (.+)$/.exec(
                carte.titre.texte
            )
            ouverture = constat
                ? `${constat[1].charAt(0).toUpperCase()}${constat[1].slice(1)} : il peut disjoncter.${carte.raison ? ` ${carte.raison}` : ""}`
                : carte.raison
            const fichePourquoi = carte.fiche.pourquoi
            if (fichePourquoi) {
                const sansRepli = fichePourquoi.texte
                    .split(". ")[0]
                    .replace(/\.$/, "")
                puces.push(`${fichePourquoi.titre} : ${sansRepli}.`)
            }
            if (carte.fiche.formuleRecalculee)
                puces.push(carte.fiche.formuleRecalculee)
            if (demande) puces.push(`À demander au fournisseur : ${demande}.`)
            break
        }
        case "rien_a_changer":
        case "option_sous_le_seuil":
        case "surcout":
            ouverture = `${ETUDE_DU_COMPTEUR} ne fait apparaître aucune économie.`
            pourquoi(carte.raison)
            break
        case "vos_puissances": {
            const montant = verdictEnMots(carte.titre)
            verdict = montant
                ? `Vos puissances : ${montant} face au contrat actuel`
                : "Vos puissances"
            ouverture = montant
                ? `${ETUDE_DU_COMPTEUR} chiffre les puissances de votre choix : ${montant} face au contrat actuel.`
                : null
            pourquoi(carte.raison)
            if (demande)
                puces.push(
                    `À demander au fournisseur, si vous retenez ces valeurs : ${demande}.`
                )
            break
        }
        case "vos_puissances_identiques":
            pourquoi(carte.raison)
            break
        case "scenario_non_recommande": {
            const montant = verdictEnMots(carte.titre)
            const nom = carte.scenarioConseille
                ? LIBELLE_SCENARIO_ARBITRAGE[
                      carte.scenarioConseille === "sans" ? "avec" : "sans"
                  ]
                : carte.titre.texte.replace(/\s*:$/, "")
            verdict = montant ? `${nom} : ${montant}` : nom
            pourquoi(carte.raison)
            if (demande)
                puces.push(
                    `À demander au fournisseur, si vous retenez ce scénario : ${demande}.`
                )
            break
        }
    }
    // C6 : la condition du raccordement, mot pour mot ; une valeur par
    // défaut à confirmer reste un geste du courtier, pas une phrase au client.
    if (
        carte.alerte?.niveau === "action" &&
        carte.alerte.ancre === "raccordement"
    )
        puces.push(`Avant d'envoyer : ${carte.alerte.texte}`)
    return { verdict, ouverture, puces, perimetre: LIGNE_DE_PERIMETRE }
}

/** Le résumé en texte brut : verdict, ouverture, puces à tiret, périmètre. */
export function texteDuResume(resume: ResumeDeLaCarte): string {
    return [
        resume.verdict,
        resume.ouverture,
        ...resume.puces.map((p) => `- ${p}`),
        resume.perimetre,
    ]
        .filter(Boolean)
        .join("\n")
}

/** Le résumé de la carte, en texte brut, ou `null`. */
export function resumeDeLaCarte(
    carte: CarteDeTete | null | undefined
): string | null {
    const blocs = resumeEnBlocs(carte)
    return blocs ? texteDuResume(blocs) : null
}

/** Le résumé composé de l'étude, avec le contexte de la paire (Q40). */
export function resumeCompose(
    etude: OptimTurpeV2Etude | null | undefined,
    ctx: Parameters<typeof lireCarteDeTete>[1] = {}
): string | null {
    return resumeDeLaCarte(lireCarteDeTete(etude, ctx))
}

/**
 * Les métadonnées de la carte (C2, C32, R-T16) : « PDL 300 009 … 23 (C2) ·
 * Étude du 05/10/2026 à 16:21 · Réf. 712409 ». Le numéro masqué au milieu ;
 * une valeur absente se tait.
 */
export function metadonneesDeLaCarte(
    pdl: string | null | undefined,
    segment: string | null | undefined,
    date: string | null | undefined,
    identifiant: string | null | undefined = null
): string | null {
    const chiffres = (pdl ?? "").replace(/\s+/g, "")
    let compteur: string | null = null
    if (chiffres.length >= 5) {
        const debut = `${chiffres.slice(0, 3)} ${chiffres.slice(3, 6)}`.trim()
        compteur = `PDL ${debut} … ${chiffres.slice(-2)}`
    } else if (chiffres) {
        compteur = `PDL ${chiffres}`
    }
    if (compteur && segment) compteur = `${compteur} (${segment})`
    const parts = [
        compteur,
        date ? `Étude du ${date}` : null,
        identifiant ? referenceCourteEtude(identifiant) : null,
    ].filter(Boolean)
    return parts.length > 0 ? parts.join(" · ") : null
}
